import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/auth';
import notify from 'devextreme/ui/notify';
import { useNavigate } from 'react-router-dom';
import {getAPIData, deleteAPIData, adminGetCompanies} from '../../api/api'
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { DataGrid, Column, HeaderFilter,
  Paging, SearchPanel, Editing, Export,
  Button as DGButton,
} from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid as xlsExportDataGrid } from 'devextreme/excel_exporter';
import './sif.scss';
import { groupBy } from '../../utils/miscUtil';

export default function SIFCompanyData() {
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();
  const [originalData, setOriginalData] = useState(null);
  const [multipleCompanies, setMultipleCompanies] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [companiesList, setCompaniesList] = useState(null);

  const onGetCompaniesSuccess = useCallback((data) => {
    setCompaniesList(data);
    getAPIData("/reports/data/sif", user, onGetDataSuccess, onError);
  }, [setCompaniesList, setLoading])

  const onGetDataSuccess = useCallback((data) => {
    let groups = user.signInUserSession.idToken.payload['cognito:groups'];
    if (groups.includes("admin")) {
      setMultipleCompanies(true);
    } else {
      let fd = groupBy(data, 'Company');
      setMultipleCompanies(Object.keys(fd).length > 1)
    }
    setOriginalData(data);
    setLoading(false);
  }, [setOriginalData, user, companiesList]);

  useEffect(() => {
    if (companiesList === null) {
      return
    }

    if (originalData === null) {
      return
    }
    let groups = user.signInUserSession.idToken.payload['cognito:groups'];
    if (groups.includes("admin")) {
      let cl = groupBy(companiesList, 'company_id');
      for (var i=0; i<originalData.length; i++) {
        originalData[i]['Reporting Company'] = cl[originalData[i]['company_id']][0]["company_name"]
      }
    }
  }, [originalData, user, companiesList]);

  const onError = useCallback((error) => {
    if (error.status === 401) {
      refreshSession();
    } else {
      notify("An error has occured. Please try again later.", 'error', 2000);
      console.log(error);
      setLoading(false);
    }
  }, [refreshSession]);

  useEffect(() => {
    if (user.signInUserSession) {
      let groups = user.signInUserSession.idToken.payload['cognito:groups']
      var has_perm=false;
      if (groups) {
        for (var i=0; i<groups.length; i++) {
          if (groups[i]==='admin') {
            has_perm=true;
            setIsAdmin(true);
            if (companiesList === null) {
              adminGetCompanies(user, onGetCompaniesSuccess, onError);
            }
            break;
          }
          if (groups[i].includes('sifrw')) {
            has_perm=true;
            getAPIData("/reports/data/sif", user, onGetDataSuccess, onError);
            break;
          }
        }
      }
      if (!has_perm) {
        notify("Insufficient permissions. Please contact the system administrator.", 'error', 5000);
        navigate('/home');
        return
      }


    }
  }, [user, navigate, onGetDataSuccess, onError, setIsAdmin, companiesList]);




  const onExporting = useCallback((e) => {
    if (e.format === "xlsx") {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Main sheet');

      xlsExportDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'AXPC-SIF-Events.xlsx');
        });
      });
    }
  }, [user, onGetDataSuccess, onError]);

  const onDeleteSuccess = useCallback((e) => {
    notify("Data deleted succesfully.", 'success', 5000);
    getAPIData("/reports/data/sif", user, onGetDataSuccess, onError);
  }, [user, onGetDataSuccess, onError])

  const onDeleteError = useCallback((error) => {
    if (error.status === 401) {
      refreshSession();
      setLoading(false);
    } else {
      notify("An error has occured. Please try again later.", 'error', 2000);
      console.log(error);
      getAPIData("/reports/data/sif", user, onGetDataSuccess, onError);
    }

  }, [user, setLoading, onGetDataSuccess, onError, refreshSession])



  const onSaving = useCallback((e) => {
    //notify("Not yet implamented.", 'warning', 5000);
    if (e.changes.length > 0){
      for (var i=0; i< originalData.length; i++) {
        if (originalData[i]['IncidentID'] === e.changes[0].key) {
          if (e.changes[0].type === "remove") {
            deleteAPIData('/reports/data/sif/event/'+originalData[i].company_id+"/"+originalData[i].IncidentID, user, onDeleteSuccess, onDeleteError);
            setLoading(true);
          }
        }
      }
    }
  }, [originalData, user, onDeleteSuccess, onDeleteError])

  const calculateAP = (rowData) => {
    if ((parseInt(rowData['Number of Fatalities']) || 0)+(parseInt(rowData['Number of Serious Injuries']) || 0)) {
      return "Actual";
    }
    return "Potential";
  }


  if(!loading) {
    return (
      <React.Fragment>
        <div className="d-flex flex-row justify-content-between">
          <h2 className={'content-block'}>Serious Injury or Fatality Company Events</h2>
          <div className="d-flex flex-row justify-content-between">
            <Button
              className="me-2"
              text="Bulk Data Upload"
              stylingMode="outlined"
              type="default"
              onClick={() => {navigate("/sifupload")}}
            />
            <Button
              className="mb-3"
              text="Add Event"
              type="default"
              onClick={() => {navigate("/sifevent")}}
            />
          </div>
        </div>
        <hr className="mt-0"></hr>
        {originalData && <div className={'content-block'}>

          <DataGrid id="dataGrid"
            dataSource={originalData}
            keyExpr="IncidentID"
            columnHidingEnabled={true}
            cellHintEnabled={true}
            wordWrapEnabled={true}
            onSaving={onSaving}
            onExporting={onExporting}
          >
            <Export enabled={true} formats={['xlsx']} />
            <HeaderFilter visible={true} />
            <SearchPanel visible={true} width="300px"/>
            {isAdmin && <Column
              dataField={'Reporting Company'}
              caption={'Reporting Company'}
              visibleIndex={0}
              width="120px"
            >
            </Column>}
            <Column
              dataField={'Company'}
              caption={'Company'}
              visibleIndex={0}
              width="120px"
              hidingPriority={multipleCompanies?24:1}
            >
            </Column>
            <Column
              dataField={'DATE OF EVENT (m/d/yyyy)'}
              caption={'Date'}
              dataType="date"
              defaultSortOrder="desc"
              visibleIndex={1}
              width="120px"
            >
            </Column>
            <Column
              dataField={'Time of Event'}
              caption={'Time'}
              visibleIndex={2}
              hidingPriority={23}
              width="190px"
            >
            </Column>
            <Column
              dataField={'Area/Basin'}
              caption={'Area/Basin'}
              visibleIndex={3}
              hidingPriority={30}
              width="150px"
            >
            </Column>
            <Column
              dataField={'State'}
              caption={'State'}
              visibleIndex={4}
              hidingPriority={24}
              width="90px"
            >
            </Column>
            <Column
              dataField={'County'}
              caption={'County'}
              visibleIndex={5}
              hidingPriority={22}
              width="120px"
            >
            </Column>
            <Column
              dataField={'Potential or Actual SIF'}
              caption={'Type'}
              calculateCellValue={calculateAP}
              visibleIndex={6}
              hidingPriority={29}
              width="90px"
            >
            </Column>
            <Column
              dataField={'Number of Fatalities'}
              caption={'Fatalities'}
              visibleIndex={7}
              hidingPriority={28}
              width="120px"
            >
            </Column>
            <Column
              dataField={'Number of Serious Injuries'}
              caption={'Injuries'}
              visibleIndex={8}
              hidingPriority={27}
              width="110px"
            >
            </Column>
            <Column
              dataField={'SIF CATEGORY'}
              caption={'Category'}
              visibleIndex={9}
              hidingPriority={26}
              width="220px"
            >
            </Column>
            <Column
              dataField={'SIF Condition'}
              caption={'Condition'}
              visibleIndex={10}
              hidingPriority={21}
              width="200px"
            >
            </Column>
            <Column
              dataField={'IOGP Life-Saving Rule Violation'}
              caption={'IOGP Life-Saving Rule Violation'}
              visibleIndex={12}
              hidingPriority={18}
              width="150px"
            >
            </Column>
            <Column
              dataField={'Discipline of Activity at Time of Event'}
              caption={'Discipline'}
              visibleIndex={13}
              hidingPriority={25}
              width="220px"
            >
            </Column>

            <Column
              dataField={'Activity at Time of Event'}
              caption={'Activity'}
              visibleIndex={14}
              hidingPriority={20}
              width="150px"
            >
            </Column>

            <Column
              dataField={'Activity Subgroup or Task'}
              caption={'Activity Subgroup or Task'}
              visibleIndex={15}
              hidingPriority={19}
              width="150px"
            >
            </Column>

            <Column
              dataField={'DESCRIPTION OF INCIDENT'}
              caption={'Description of Incident'}
              visibleIndex={16}
              hidingPriority={13}
              width="150px"
            >
            </Column>

            <Column
              dataField={'Was it reasonable that a person was or could have been in proximity at the time of the event?'}
              caption={'Was it reasonable that a person was or could have been in proximity at the time of the event?'}
              visibleIndex={17}
              hidingPriority={16}
              width="150px"
            >
            </Column>

            <Column
              dataField={'Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?'}
              caption={'Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?'}
              visibleIndex={18}
              hidingPriority={15}
              width="150px"
            >
            </Column>

            <Column
              dataField={'Primary Causal Factors'}
              caption={'Primary Causal Factors'}
              visibleIndex={19}
              hidingPriority={17}
              width="150px"
            >
            </Column>

            <Column
              dataField={'Cause Description'}
              caption={'Cause Description'}
              visibleIndex={20}
              hidingPriority={12}
              width="150px"
            >
            </Column>
            <Column
              dataField={'What is the status of the Causal Investigation?'}
              caption={'Investigation Status'}
              visibleIndex={21}
              hidingPriority={14}
              width="150px"
            >
            </Column>


            <Column type="buttons" visibleIndex={30}>
              <DGButton
                  text="Edit"
                  icon="fas fa-pen fs-6"
                  hint="Edit"
                  onClick={(e) => {
                    navigate("/sifevent?company_id="+e.row.data.company_id+"&event_id="+e.row.data.IncidentID)
                  }}
              />
              <DGButton name="delete" />
            </Column>

            <Editing
              allowEditing={false}
              mode="popup"
              allowUpdating={true}
              allowDeleting={true}
              // allowAdding={true}
            />
            <Paging defaultPageSize={100} />
          </DataGrid>
        </div>}
      </React.Fragment>
    )
  } else {
    return (
      <div className="d-flex flex-row justify-content-center align-items-center mt-5 pt-5">
          <LoadIndicator
              id="large-indicator"
              height={60}
              width={60}
          />
      </div>
    )
  }

}
